import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useAppContext } from '@/hooks'

export type PromptDialogProps = {
	open: boolean
	title: string
	defaultValue?: string
	label?: string
	confirmLabel?: string
	cancelLabel?: string
	multiline?: boolean
	required?: boolean
	onCancel: () => void
	onConfirm: (value: string) => void
}

/** App-styled text prompt — replaces browser `window.prompt`. */
export const PromptDialog = ({
	open,
	title,
	defaultValue = '',
	label,
	confirmLabel,
	cancelLabel,
	multiline = false,
	required = false,
	onCancel,
	onConfirm,
}: PromptDialogProps) => {
	const { t } = useAppContext()
	const [value, setValue] = useState(defaultValue)

	useEffect(() => {
		if (open) setValue(defaultValue)
	}, [defaultValue, open])

	const submit = () => {
		const trimmed = value.trim()
		if (required && !trimmed) return
		onConfirm(value)
	}

	return (
		<Dialog
			open={open}
			onClose={onCancel}
			maxWidth="sm"
			fullWidth
			data-testid="prompt-dialog"
		>
			<DialogTitle
				sx={{ fontSize: 18, fontWeight: 700, color: '#1F4954', pb: 1 }}
			>
				{title}
			</DialogTitle>
			<DialogContent sx={{ pt: '10px !important' }}>
				<TextField
					autoFocus
					fullWidth
					label={label}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					multiline={multiline}
					minRows={multiline ? 3 : undefined}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !multiline) {
							e.preventDefault()
							submit()
						}
						if (e.key === 'Escape') {
							e.preventDefault()
							onCancel()
						}
					}}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2.5 }}>
				<Button onClick={onCancel} color="inherit">
					{cancelLabel ?? t('CANCEL')}
				</Button>
				<Button
					onClick={submit}
					variant="contained"
					disabled={required && !value.trim()}
					sx={{ bgcolor: '#1F4954', '&:hover': { bgcolor: '#163840' } }}
				>
					{confirmLabel ?? t('OK')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
